// JMUI TODO Fix rules of hooks violations
import type Sentiment from "../../../../../../../../../shared/util/salary/Sentiment";
import type ViewType from "../../../../constants/ViewType";
import type InlineQuestionResponse from "../../../../types/InlineQuestionResponse";
import type { QualificationAttribute } from "../../../../types/JobAttribute";
import type { TileSectionProps } from "../../../common/TileSection";

import {
  getMissingRequirement,
  getRequirementsTitleText,
  sortRequirements,
  getYellowLightSectionIcon,
  getYellowLightTileList,
  getTileSectionSentimentCount,
} from "../";
import { type GettextInterface, useI18n } from "../../../../../../../../../shared/I18n";
import { useLogging } from "../../../../context/useLogging";
import YellowLightCategoryType from "../../../../types/YellowLightCategoryType";
import { useShowCount } from "../../../../utils/useShowCount";
import { useQualificationTiles } from "../useQualificationTiles";

import { useEffect } from "react";

export const getInlineQuestion = (
  { type, label }: QualificationAttribute,
  gt: GettextInterface,
) => {
  const defaultLabel = gt.gettext("Do you have experience in [{0}|attribute]?", [label]);
  const labels: { [key: string]: string } = {
    [YellowLightCategoryType.LICENSE]: gt.gettext("Do you have a valid [{0}|attribute] license?", [
      label,
    ]),
    [YellowLightCategoryType.CERTIFICATIONS]: gt.gettext(
      "Do you have a valid [{0}|attribute] certification?",
      [label],
    ),
    [YellowLightCategoryType.EDUCATION]: gt.gettext("Do you have a [{0}|attribute]?", [label]),
    [YellowLightCategoryType.LANGUAGES]: gt.gettext("Do you know [{0}|attribute]?", [label]),
    [YellowLightCategoryType.SKILLS]: defaultLabel,
  };

  return labels[type];
};

export const useQualificationSection = ({
  category,
  attributes,
  answeredIds,
  onModalSubmit,
  onInlineSubmit,
  onModalReset,
}: {
  category: YellowLightCategoryType;
  attributes: QualificationAttribute[];
  answeredIds: string[];
  onModalSubmit: (p: {
    attribute: QualificationAttribute;
    sentiment: Sentiment;
  }) => Promise<boolean>;
  onInlineSubmit: (p: {
    response: InlineQuestionResponse;
    attribute: QualificationAttribute;
  }) => Promise<boolean>;
  onModalReset: (r: QualificationAttribute) => Promise<boolean>;
  viewType: ViewType;
}): TileSectionProps => {
  const logger = useLogging();
  const gt = useI18n();

  const reqs: QualificationAttribute[] = sortRequirements(attributes, category, answeredIds);

  const tileList: QualificationAttribute[] = getYellowLightTileList(
    reqs,
    useShowCount(reqs.length),
    answeredIds,
  );

  const missingRequirement = getMissingRequirement(reqs, category, answeredIds);

  const logVisibleAttributes = () => {
    logger.logAttributeTilesShownForSection(reqs, answeredIds ?? [], category?.toString() ?? "");
  };

  useEffect(logVisibleAttributes, [reqs, answeredIds, logger, category]);

  useEffect(() => {
    if (missingRequirement) {
      logger.logDataCollectionShown(category.toString());
      logger.logQualificationQuestionRendered(missingRequirement, "");
    }
  }, [category, logger, missingRequirement]);

  return {
    type: category,
    inlineQuestionLabel: missingRequirement ? getInlineQuestion(missingRequirement, gt) : undefined,
    tiles: tileList.map((attribute) =>
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useQualificationTiles({
        attribute,
        answeredIds,
        onModalSubmit: (params) => onModalSubmit(params),
        onModalReset,
      }),
    ),
    title: getRequirementsTitleText(category, gt),
    icon: getYellowLightSectionIcon(category),
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- FIXME
    onSubmit: missingRequirement
      ? async (response: InlineQuestionResponse) => {
          await onInlineSubmit({
            response,
            attribute: missingRequirement,
          });
        }
      : undefined,
    sentimentCounts: getTileSectionSentimentCount(tileList),
  };
};
